'use client';

import React, { useRef, useEffect } from 'react';
import SectionHeader from '@/app/components/SectionHeader';
import Card from '@/app/components/Card';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import { useMedia } from "@/hooks/useMedia";
import { MOBILE_QUERY } from "@/hooks/breakpoints";
import { useIsVisible } from '@/hooks/useIsVisible';
import { useNearViewport } from '@/hooks/useNearViewport';

const cases = [
    {
        slug: 'neo-bank',
        titleKey: 'caseNeoBankTitle',
        descKey: 'caseNeoBankDesc',
        image: '/images/case-neobank.webp',
    },
    {
        slug: 'med-platform',
        titleKey: 'caseMedPlatformTitle',
        descKey: 'caseMedPlatformDesc',
        image: '/images/case-medplatform.webp',
    },
    {
        slug: 'ecom-accelerator',
        titleKey: 'caseEcomAcceleratorTitle',
        descKey: 'caseEcomAcceleratorDesc',
        image: '/images/case-ecommerce.webp',
    },
];

export default function WorkSection() {
    const { t } = useComponentTranslations("WorkSection");
    const locale = useLocale();
    const mobile = useMedia(MOBILE_QUERY);
    const swiperRef = useRef<HTMLDivElement>(null);
    const swiperInstanceRef = useRef<{ autoplay: { start: () => void; stop: () => void }; destroy: () => void } | null>(null);
    const { ref: nearRef, near } = useNearViewport('600px');
    const { ref: sectionRef, isVisible } = useIsVisible(0.5);

    useEffect(() => {
        if (!mobile || !near || !swiperRef.current) return;
        let cancelled = false;

        Promise.all([
            import('@/app/components/swiper-lite'),
            import('swiper/css').catch(() => null),
        ]).then(([mod]) => {
            if (cancelled || !swiperRef.current) return;
            if ((swiperRef.current as { swiper?: unknown }).swiper) return;

            const swiper = mod.createAutoplaySwiper(swiperRef.current, {
                slidesPerView: 1.2,
                spaceBetween: 16,
                loop: true,
                autoplay: {
                    delay: 3500,
                    disableOnInteraction: false,
                },
            });
            swiperInstanceRef.current = swiper;
            swiper.autoplay.stop();
        });

        return () => {
            cancelled = true;
            swiperInstanceRef.current?.destroy();
            swiperInstanceRef.current = null;
        };
    }, [mobile, near]);

    useEffect(() => {
        const swiper = swiperInstanceRef.current;
        if (!swiper) return;
        if (isVisible) {
            swiper.autoplay.start();
        } else {
            swiper.autoplay.stop();
        }
    }, [isVisible]);

    return (
        <section ref={sectionRef} className="py-10 max-md:py-9">
            <div ref={nearRef} className="mx-auto max-w-7xl px-4">
                <SectionHeader title={t('sectionWork')} href={getPathWithLocale("/work", locale)} align="left" />

                {mobile ? (
                    <div ref={swiperRef} className="swiper mt-6">
                        <div className="swiper-wrapper">
                            {cases.map((item) => (
                                <div className="swiper-slide" key={item.slug}>
                                    <Card
                                        href={getPathWithLocale(`/work/${item.slug}`, locale)}
                                        title={t(item.titleKey, item.slug.replace(/-/g, ' '))}
                                        description={t(item.descKey, 'Case study →')}
                                        imageUrl={item.image}
                                        buttonText={t('caseButton', 'Learn More')}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {cases.map((item) => (
                            <Card
                                key={item.slug}
                                href={getPathWithLocale(`/work/${item.slug}`, locale)}
                                title={t(item.titleKey, item.slug.replace(/-/g, ' '))}
                                description={t(item.descKey, 'Case study →')}
                                imageUrl={item.image}
                                buttonText={t('caseButton', 'Learn More')}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
